import { Injectable, inject } from '@angular/core';
import { MetaDefinition } from '@angular/platform-browser';
import { envs } from '@environments';
import { Episode, EpisodeReleaseStatus, ProgramWithSeasons } from '@shared/graphql/api.generated';
import { ogSiteName } from '@shared/helper/configs';
import { LinkDefinition } from '@shared/seo/link.service';
import { SeoService } from '@shared/seo/seo.service';
import { SchemaService } from './schema.service';

@Injectable()
export class EpisodeSeoService {
  private readonly seoService = inject(SeoService);
  private readonly schemaService = inject(SchemaService);

  setSeoTags(episode: Episode, program: ProgramWithSeasons) {
    this.seoService.removeMetaTags();
    this.setTitle(episode, program);
    this.setMetaTags(episode, program);
    this.setLinkTags(episode, program);
  }

  private episodeTitle(episode?: Episode, program?: ProgramWithSeasons): string {
    if (program?.program?.isSolitary) {
      return episode?.title!;
    }
    if (program?.seasons.length === 1) {
      return `${episode?.title} ${program?.program?.title}`;
    }
    const seasonTitle = program?.seasons.find((item) => item?.id === episode?.seasonID)?.title;
    if (episode?.order === 1) {
      return `${seasonTitle} ${program?.program?.title}`;
    }
    return `${episode?.title} ${seasonTitle} ${program?.program.title}`;
  }

  private programTitle(program: ProgramWithSeasons): string {
    return (program.program.isSolitary ? 'فیلم ' : 'سریال ') + program.program.title;
  }

  private handleEpisodeTitle(episode: Episode, program: ProgramWithSeasons): string {
    const constanceTitle = ' - تماشای آنلاین در شیدا';
    return `${this.episodeTitle(episode, program)}${constanceTitle}`;
  }

  private handleEpisodeDescription(episode: Episode, program: ProgramWithSeasons): string {
    return program.program.isSolitary ? program.program.summary : episode.summary;
  }

  private handleEpisodeUrl(episode: Episode, program: ProgramWithSeasons): string {
    return (
      envs.siteUrl +
      (program.program.isSolitary ? `/p/${program.program.uid}` : `/e/${episode.uid}`)
    );
  }

  private handleEpisodeKeyword(episode: Episode, program: ProgramWithSeasons): string {
    const keywords = [
      'فیلم',
      'سریال',
      `پخش آنلاین ${this.episodeTitle(episode, program)}`,
      `دانلود ${this.episodeTitle(episode, program)}`,
      `مشاهده ${this.episodeTitle(episode, program)}`,
      `مشاهده آنلاین ${this.episodeTitle(episode, program)}`,
    ];
    return keywords.join(', ');
  }

  private setTitle(episode: Episode, program: ProgramWithSeasons) {
    this.seoService.setTitle(this.handleEpisodeTitle(episode, program));
  }

  private setMetaTags(episode: Episode, program: ProgramWithSeasons) {
    const tags: MetaDefinition[] = [
      {
        name: 'title',
        content: this.handleEpisodeTitle(episode, program),
      },
      {
        name: 'description',
        content: this.handleEpisodeDescription(episode, program),
      },
      {
        name: 'keywords',
        content: this.handleEpisodeKeyword(episode, program),
      },
      {
        property: 'og:url',
        content: this.handleEpisodeUrl(episode, program),
      },
      {
        property: 'og:title',
        content: this.handleEpisodeTitle(episode, program),
      },
      {
        property: 'og:description',
        content: this.handleEpisodeDescription(episode, program),
      },
      {
        property: 'og:image',
        content: episode.landscapeImagePath,
      },
      {
        property: 'og:site_name',
        content: ogSiteName,
      },
      {
        property: 'og:type',
        content: 'video.episode',
      },
      // {
      //   property: 'og:video',
      //   content: 'trailer url',
      // },
      // {
      //   property: 'og:video:secure_url',
      //   content: 'trailer url',
      // },
      {
        property: 'og:image:width',
        content: '1280',
      },
      {
        property: 'og:image:height',
        content: '720',
      },
      {
        name: 'twitter:card',
        content: 'summary_large_image',
      },
      {
        name: 'twitter:title',
        content: this.handleEpisodeTitle(episode, program),
      },
      {
        name: 'twitter:description',
        content: this.handleEpisodeDescription(episode, program),
      },
      {
        name: 'twitter:image',
        content: episode.landscapeImagePath,
      },
    ];
    this.seoService.setMetaTags(tags);
  }

  private setLinkTags(episode: Episode, program: ProgramWithSeasons) {
    const tags: LinkDefinition[] = [
      {
        rel: 'image_src',
        href: episode.landscapeImagePath,
      },
      {
        rel: 'thumbnailUrl',
        href: episode.landscapeImagePath,
      },
      {
        rel: 'canonical',
        href: this.handleEpisodeUrl(episode, program),
      },
    ];
    this.seoService.setLinkTag(tags);
  }

  setEpisodeBreadcrumbsJsonLd(episode: Episode, program: ProgramWithSeasons) {
    this.schemaService.setJsonLd({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'شیدا',
          item: `${envs.siteUrl}/`,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: program.program.isSolitary ? 'فیلم سینمایی' : 'سریال',
          item: `${envs.siteUrl}/c/${program.program.isSolitary ? 'movie' : 'series'}`,
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: this.programTitle(program),
          item: `${envs.siteUrl}/p/${program.program.uid}`,
        },
        {
          '@type': 'ListItem',
          position: 4,
          name: this.episodeTitle(episode, program),
        },
      ],
    });
  }

  setEpisodeJsonLd(episode: Episode, program: ProgramWithSeasons) {
    let defaultTrailer = program.trailers.find((trailer) => trailer?.isDefault);
    if (!defaultTrailer) defaultTrailer = program.trailers[0];
    const jsonData: Record<string, any> = {
      '@context': 'https://schema.org',
      '@type': 'TVEpisode',
      episodeNumber: episode.order,
      description: this.handleEpisodeDescription(episode, program),
      dateModified: episode.updatedAt,
      name: this.episodeTitle(episode, program),
      url: `${envs.siteUrl}/e/${episode.uid}`,
      partOfSeries: {
        '@type': 'TVSeries',
        image: program.program.bannerImagePath,
        description: program.program.description,
        name: this.programTitle(program),
        url: `${envs.siteUrl}/p/${program.program.uid}`,
      },
      image: {
        '@type': 'ImageObject',
        url: episode.landscapeImagePath,
      },
      dateCreated: episode.createdAt,
      actor: program.cast
        .find((cast) => cast?.role === 'PERFORMER')
        ?.artists?.map((cast) => ({
          '@type': 'Person',
          name: cast?.fullName,
        })),
      director: {
        '@type': 'Person',
        name: program.cast
          .find((cast) => cast?.role === 'DIRECTOR')
          ?.artists?.map((director) => director.fullName)
          .join(', '),
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        bestRating: '100',
        description:
          'The Sheyda rating is a measurement of movie and Series quality for moviegoers. It represents the percentage of viewers reviews that are positive for a given film or series.',
        ratingCount: episode.statisticsDetails?.totalInterestCount.toString(),
        ratingValue: (
          episode.statisticsDetails?.totalLikePercentage! +
          episode.statisticsDetails?.totalLovePercentage!
        ).toString(),
        worstRating: '0',
      },
      genre: program.program.genres?.map((genre) => genre.name),
      // TODO: Handle it
      // "subtitleLanguage": ["en", "fa"],
      // "inLanguage": ["fa", "en"],
      trailer: {
        '@type': 'VideoObject',
        name: defaultTrailer!.title,
        description: this.handleEpisodeDescription(episode, program),
        thumbnailUrl: defaultTrailer!.coverImagePath,
        duration: this.schemaService.handleDuration(defaultTrailer!.duration),
        contentUrl: `${envs.siteUrl}/pr/${defaultTrailer!.id}`,
        embedUrl: `${envs.siteUrl}/pr/${defaultTrailer!.id}`,
        uploadDate: defaultTrailer!.createdAt,
        url: `${envs.siteUrl}/pr/${defaultTrailer!.id}`,
      },
      publisher: {
        '@type': 'Organization',
        name: 'پلتفرم فیلم و سریال شیدا',
        '@id': `${envs.siteUrl}`,
      },
      video: {
        '@type': 'VideoObject',
        name: this.episodeTitle(episode, program),
        description: this.handleEpisodeDescription(episode, program),
        thumbnailUrl: episode.landscapeImagePath,
        uploadDate: episode.createdAt,
      },
      // TODO: Fix
      // "interactionStatistic": {
      //   "@type": "InteractionCounter",
      //   "userInteractionCount": "11"
      // }
    };

    if (episode.releaseDate) {
      jsonData['datePublished'] = episode.releaseDate;
    } else if (episode.releaseStatus === EpisodeReleaseStatus.Released) {
      jsonData['datePublished'] = episode.createdAt;
    }

    this.schemaService.setJsonLd(jsonData);
  }
}
